import type { CommentSort, CommentVoteValue } from "@pax/api-contract/comments/v1alpha1";
import {
  type InfiniteData,
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
} from "@tanstack/react-query";
import type { Session } from "@/features/auth/queries";
import { useSession } from "@/features/auth/queries";
import { api, publicApi, publicOrpc, reachable } from "@/lib/api";

type CommentsPage = Awaited<ReturnType<typeof publicApi.comments.list>>;
type RepliesPage = Awaited<ReturnType<typeof publicApi.comments.replies.list>>;
type CommentThread = Awaited<ReturnType<typeof publicApi.comments.get>>;
export type CommentView = CommentThread["comment"];
export type ReplySiblings = NonNullable<CommentThread["siblings"]>;

/** A preset opens on a few comments; asking for more loads a fuller page. */
const FIRST_COMMENTS = 3;
const COMMENTS_PAGE_SIZE = 10;
const REPLIES_PAGE_SIZE = 5;

/**
 * Who is reading. Comment responses carry the reader's vote and permissions,
 * so every comment query is keyed by it: signing in, out, or upgrading a guest
 * reads a fresh cache entry instead of another reader's.
 */
export type CommentViewer = { id: string; guest: boolean } | null;

export function commentViewerOf(session: Session | null | undefined): CommentViewer {
  return session ? { id: session.user.id, guest: session.user.isAnonymous === true } : null;
}

export function useCommentViewer(): CommentViewer {
  return commentViewerOf(useSession().data);
}

const listKey = publicOrpc.comments.list.key();
const repliesKey = publicOrpc.comments.replies.list.key();
const threadKey = publicOrpc.comments.get.key();

export const presetCommentsQuery = (slug: string, sort: CommentSort, viewer: CommentViewer) =>
  infiniteQueryOptions({
    queryKey: [...listKey, { slug, sort, viewer }],
    queryFn: ({ pageParam }) =>
      reachable(
        publicApi.comments.list({
          query: pageParam
            ? { presetSlug: slug, sort, limit: COMMENTS_PAGE_SIZE, cursor: pageParam }
            : { presetSlug: slug, sort, limit: FIRST_COMMENTS },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: CommentsPage) => lastPage.nextCursor,
    staleTime: 30_000,
  });

export const commentRepliesQuery = (commentId: string, viewer: CommentViewer) =>
  infiniteQueryOptions({
    queryKey: [...repliesKey, { commentId, viewer }],
    queryFn: ({ pageParam }) =>
      reachable(
        publicApi.comments.replies.list({
          params: { commentId },
          query: { limit: REPLIES_PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: RepliesPage) => lastPage.nextCursor,
    staleTime: 30_000,
  });

/** A reply's older or newer siblings, paged outward from the cursor `get` returned for it. */
export const siblingRepliesQuery = (parentId: string, fromCursor: string, viewer: CommentViewer) =>
  infiniteQueryOptions({
    queryKey: [...repliesKey, { commentId: parentId, viewer, fromCursor }],
    queryFn: ({ pageParam }) =>
      reachable(
        publicApi.comments.replies.list({
          params: { commentId: parentId },
          query: { limit: REPLIES_PAGE_SIZE, cursor: pageParam },
        }),
      ),
    initialPageParam: fromCursor,
    getNextPageParam: (lastPage: RepliesPage) => lastPage.nextCursor,
    staleTime: 30_000,
  });

export const commentThreadQuery = (commentId: string, viewer: CommentViewer) =>
  queryOptions({
    queryKey: [...threadKey, { commentId, viewer }],
    queryFn: () => reachable(publicApi.comments.get({ params: { commentId } })),
    staleTime: 30_000,
  });

/** Each comment once, in page order; a vote or pin can move one between pages. */
export function uniqueById<T extends { id: string }>(comments: T[]): T[] {
  const seen = new Set<string>();
  return comments.filter(comment => !seen.has(comment.id) && seen.add(comment.id));
}

/** Rewrites one comment in every cached list, reply page, and permalink that holds it. */
function updateCachedComment(
  queryClient: QueryClient,
  commentId: string,
  update: <T extends CommentView>(comment: T) => T,
): void {
  const apply = <T extends CommentView>(comment: T): T =>
    comment.id === commentId ? update(comment) : comment;
  queryClient.setQueriesData<InfiniteData<CommentsPage>>({ queryKey: listKey }, data =>
    data
      ? {
          ...data,
          pages: data.pages.map(page => ({
            ...page,
            pinned: page.pinned.map(apply),
            comments: page.comments.map(apply),
          })),
        }
      : data,
  );
  queryClient.setQueriesData<InfiniteData<RepliesPage>>({ queryKey: repliesKey }, data =>
    data
      ? { ...data, pages: data.pages.map(page => ({ ...page, replies: page.replies.map(apply) })) }
      : data,
  );
  queryClient.setQueriesData<CommentThread>({ queryKey: threadKey }, data =>
    data
      ? { ...data, comment: apply(data.comment), parent: data.parent && apply(data.parent) }
      : data,
  );
}

/** Adds `change` to the published count every cached listing of `slug` reports. */
function adjustCommentCount(queryClient: QueryClient, slug: string, change: number): void {
  queryClient.setQueriesData<InfiniteData<CommentsPage>>(
    { queryKey: [...listKey, { slug }] },
    data =>
      data
        ? {
            ...data,
            pages: data.pages.map(page => ({ ...page, commentCount: page.commentCount + change })),
          }
        : data,
  );
}

function invalidateListings(queryClient: QueryClient, slug: string): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: [...listKey, { slug }] });
}

export async function createComment(
  queryClient: QueryClient,
  slug: string,
  body: string,
  parentId?: string,
): Promise<CommentView> {
  const { comment } = await reachable(
    api.comments.create({
      body: { presetSlug: slug, body, ...(parentId ? { parentId } : {}) },
    }),
  );
  adjustCommentCount(queryClient, slug, 1);
  if (parentId) {
    updateCachedComment(queryClient, parentId, parent => ({
      ...parent,
      replyCount: parent.replyCount + 1,
    }));
    void queryClient.invalidateQueries({ queryKey: [...repliesKey, { commentId: parentId }] });
  } else {
    // Show the new comment at the top of every loaded sort; a later refetch
    // puts it where the sort ranks it, and `uniqueById` hides the duplicate.
    queryClient.setQueriesData<InfiniteData<CommentsPage>>(
      { queryKey: [...listKey, { slug }] },
      data => {
        const [first, ...rest] = data?.pages ?? [];
        if (!data || !first || comment.parentId !== null) return data;
        return {
          ...data,
          pages: [
            { ...first, comments: [{ ...comment, parentId: null }, ...first.comments] },
            ...rest,
          ],
        };
      },
    );
  }
  return comment;
}

export async function editComment(
  queryClient: QueryClient,
  commentId: string,
  body: string,
): Promise<void> {
  const { comment } = await reachable(
    api.comments.update({ params: { commentId }, body: { body } }),
  );
  updateCachedComment(queryClient, commentId, cached => ({
    ...cached,
    ...comment,
    parentId: cached.parentId,
  }));
}

export async function deleteComment(
  queryClient: QueryClient,
  slug: string,
  comment: CommentView,
): Promise<void> {
  await reachable(api.comments.delete({ params: { commentId: comment.id } }));
  // The response is empty, so build the tombstone the list would now return.
  updateCachedComment(queryClient, comment.id, cached => ({
    ...cached,
    status: "deleted",
    body: null,
    mentions: [],
    pinnedAt: null,
    ...(cached.viewer ? { viewer: { ...cached.viewer, canEdit: false, canPin: false } } : {}),
  }));
  if (comment.status === "published") adjustCommentCount(queryClient, slug, -1);
  // A deleted pin leaves the pinned list and returns to its sorted place.
  if (comment.pinnedAt) void invalidateListings(queryClient, slug);
}

export async function voteComment(
  queryClient: QueryClient,
  commentId: string,
  value: CommentVoteValue | null,
): Promise<void> {
  const { likes } = await reachable(
    value
      ? api.comments.vote.set({ params: { commentId }, body: { value } })
      : api.comments.vote.delete({ params: { commentId } }),
  );
  // Patch rather than refetch: under `top` a refetch would move the comment.
  updateCachedComment(queryClient, commentId, cached => ({
    ...cached,
    likes,
    ...(cached.viewer ? { viewer: { ...cached.viewer, vote: value } } : {}),
  }));
}

export async function setCommentPinned(
  queryClient: QueryClient,
  slug: string,
  commentId: string,
  pinned: boolean,
): Promise<void> {
  await reachable(
    pinned
      ? api.comments.pin.set({ params: { commentId } })
      : api.comments.pin.delete({ params: { commentId } }),
  );
  // Pinning moves a comment between the pinned list and the sorted pages.
  await invalidateListings(queryClient, slug);
}
